/**
 * prd2 Week 2 Day 10: bust meter + near-miss (slow-mo + “CLOSE CALL” elsewhere).
 */
export type BustMeterState = {
  meter: number;
  peak: number;
  wasInDanger: boolean;
};

export function createBustMeterState(): BustMeterState {
  return { meter: 0, peak: 0, wasInDanger: false };
}

export const BUST = {
  /** Inside this radius (m) the bust ring fills — tight so cops can “pressure” without instant game over. */
  DANGER_RADIUS: 12,
  /** Outside this radius the meter decays quickly — escape. */
  SAFE_RADIUS: 28,
  /** Slower fill so ring / close chase reads before bust. */
  FILL_PER_S: 0.16,
  DECAY_PER_S: 0.5,
  DECAY_MID_PER_S: 0.2,
} as const;

export type BustStepResult = {
  meter: number;
  busted: boolean;
  nearMiss: boolean;
};

export function stepBustMeter(
  s: BustMeterState,
  dist: number,
  dt: number,
): BustStepResult {
  const inDanger = dist < BUST.DANGER_RADIUS;
  const inSafe = dist > BUST.SAFE_RADIUS;

  if (inDanger) {
    s.meter += dt * BUST.FILL_PER_S;
    if (s.meter > s.peak) s.peak = s.meter;
  } else if (inSafe) {
    s.meter -= dt * BUST.DECAY_PER_S;
  } else {
    s.meter -= dt * BUST.DECAY_MID_PER_S;
  }

  s.meter = Math.max(0, Math.min(1, s.meter));

  const busted = s.meter >= 0.995;

  const nearMiss =
    !inDanger &&
    s.wasInDanger &&
    s.peak >= 0.48 &&
    s.meter < 0.38 &&
    inSafe;

  if (nearMiss) {
    s.peak = 0;
  }

  s.wasInDanger = inDanger;

  return { meter: s.meter, busted, nearMiss };
}
